/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Aug 02 2024
 **/

import { isEmpty } from "lodash-es";
import { Updater, useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/frameworks/designStuff/components";
import { _T, _P } from "@rfice/language";

import { PeopleList } from "./peopleTypes";
import { useFriendListActions } from "../useFriendServiceManager";
import { APIErrorCode } from "@/frameworks/core/commons";

type PeopleListMap = PeopleList;
type PeopleChangeData = { callFavorite: boolean } | { friend: boolean } | { blockedByMe: boolean };

type PeopleUpdateAction =
	| { type: "add" | "delete"; payload: { groupID: string; userID: string } }
	| { type: "delete"; payload: { userID: string } }
	| { type: "bookmark"; payload: { callFavorite: boolean; groupID: string; userID: string } }
	| { type: "block"; payload: { active: boolean; userID: string } };

export default function usePeopleManagerHandler() {
	const queryClient = useQueryClient();
	const toast = useToast();

	const setPeopleList = (updater: Updater<PeopleListMap | undefined, PeopleListMap | undefined>) => {
		queryClient.setQueryData<PeopleListMap>(["getPeopleList"], updater);
	};

	const { update } = useFriendListActions();

	const updatePeople = ({ type, payload }: PeopleUpdateAction) => {
		switch (type) {
			case "add":
				return Promise.resolve(
					update({ type, payload }).then((res) => {
						if (res?.status !== 200) return;
						const data = queryClient.getQueryData<PeopleList>(["getPeopleList"]);
						if (!data) return;
						let userName = "";
						const result = new Map() as PeopleList;

						data.forEach((item) => {
							if (!isEmpty(item.users.get(payload.userID))) {
								item.users.set(payload.userID, {
									...item.users.get(payload.userID),
									friend: true
								} as WebServices.People.List.Item["users"][number]);
								userName = String(item.users.get(payload.userID)?.name);
							}

							result.set(item.groupId, item);
						});

						setPeopleList((prev) => {
							if (!prev) return prev;

							return result;
						});

						toast.show({
							type: "success",
							message: _P("{{name}} 님과 친구가 되었습니다.", {
								name: userName
							}) as string
						});
					})
				).catch((error) => {
					if (error.code === APIErrorCode.FRIEND_REQUEST_FAILED) {
						toast.show({
							type: "system",
							message: _T("친구 요청 권한이 없습니다.")
						});
					}
				});
			case "delete":
				return Promise.resolve(
					update({ type, payload }).then((res) => {
						if (res?.status !== 200) return;
						const data = queryClient.getQueryData<PeopleList>(["getPeopleList"]);
						if (!data) return;
						const result = new Map() as PeopleList;

						data.forEach((item) => {
							if (!isEmpty(item.users.get(payload.userID))) {
								item.users.set(payload.userID, {
									...item.users.get(payload.userID),
									friend: false
								} as WebServices.People.List.Item["users"][number]);
							}

							result.set(item.groupId, item);
						});

						setPeopleList((prev) => {
							if (!prev) return prev;

							return result;
						});
					})
				);
			case "bookmark":
				return Promise.resolve(
					update({ type, payload: { active: !payload.callFavorite, userID: payload.userID } }).then((res) => {
						if (res?.status !== 200) return;
						setPeopleList((prev) => {
							if (!prev) return prev;

							return updateUser(prev, {
								groupID: payload.groupID,
								userID: payload.userID,
								changeData: { callFavorite: !payload.callFavorite }
							});
						});
					})
				);
			case "block":
				return Promise.resolve(
					update({ type, payload: { active: payload.active, userID: payload.userID } }).then((res) => {
						if (res?.status !== 200) return;

						const data = queryClient.getQueryData<PeopleList>(["getPeopleList"]);
						if (!data) return;
						const result = new Map() as PeopleList;

						data.forEach((item) => {
							if (!isEmpty(item.users.get(payload.userID))) {
								item.users.set(payload.userID, {
									...item.users.get(payload.userID),
									blockedByMe: !item.users.get(payload.userID)?.blockedByMe
								} as WebServices.People.List.Item["users"][number]);
							}

							result.set(item.groupId, item);
						});

						setPeopleList((prev) => {
							if (!prev) return prev;
							return result;
						});
					})
				);
			default:
				return;
		}
	};

	const updateUser = (
		data: PeopleList,
		payload: { groupID: string; userID: string; changeData: PeopleChangeData }
	) => {
		const group = data.get(payload.groupID);
		const updateUsers = group?.users;
		updateUsers?.set(payload.userID, {
			...group?.users.get(payload.userID),
			...payload.changeData
		} as WebServices.People.List.Item["users"][number]);
		return data;
	};

	return {
		updatePeople
	};
}
